import type { LandingCardEntityBase } from "@/components/landing/landingCard";
import { LE, LE_CODES } from "@/constants/enums";
import { formatDustOrMonitorCardNumber } from "@/helpers/cardNumbers";
import { getElectronicFormRouteNameFromItem } from "@/helpers/electronicFormHelpers";
import { routeNames } from "@/routes";
import type { DustCard } from "@/views/dustCard/dustCard";
import type { DustClass } from "@/views/dustClass/dustClass";
import type { DustMonitorCard } from "@/views/dustMonitorCard/dustMonitorCard";
import type { DustMonitorClass } from "@/views/dustMonitorClass/dustMonitorClass";
import { getDustMonitorClassDisplayDate } from "@/views/dustMonitorClass/dustMonitorClassApi";
import type {
  DustClassRegistrationGroupForm,
  DustClassRegistrationSingleForm,
  DustMonitorClassRegistrationForm,
  ElectronicForm,
} from "@/views/electronicForm/electronicForm";
import { fromDateAndTimeToFormat, sysDate } from "@modernary/mui/utils/filters";
import { getMurnParts } from "@modernary/mui/utils/helpers";
import { addMilliseconds, isAfter, isBefore, isEqual, parseISO, startOfDay } from "date-fns";

export interface CardsAndClassesItem extends LandingCardEntityBase {
  primaryLabel: string;
  secondaryLabel: string;
  additionalInfo?: string;
  additionalInfoDetail?: string;
  emphasizeAdditionalInfo?: boolean;
  sortValue?: string;
}

export interface CardsAndClassesGroup {
  cards: CardsAndClassesItem[];
  scheduledClasses: CardsAndClassesItem[];
  awaitingScheduling: CardsAndClassesItem[];
}

export interface CardsAndClassesGroups {
  mine: CardsAndClassesGroup;
  others: CardsAndClassesGroup;
}

type OwnerGroup = keyof CardsAndClassesGroups;
const HOME_SECTION_ITEM_LIMIT = 5;
type RegistrationForm =
  | DustClassRegistrationSingleForm
  | DustClassRegistrationGroupForm
  | DustMonitorClassRegistrationForm;

interface RegistrationParticipant {
  firstName?: string;
  lastName?: string;
  email?: string;
  company?: string;
  cardNumber?: string;
}

interface ClassRegistrationDetails {
  owners: Set<OwnerGroup>;
  others: RegistrationParticipant[];
}

const emptyGroup = (): CardsAndClassesGroup => ({
  cards: [],
  scheduledClasses: [],
  awaitingScheduling: [],
});

export const emptyCardsAndClassesGroups = (): CardsAndClassesGroups => ({
  mine: emptyGroup(),
  others: emptyGroup(),
});

export const hasCardsAndClassesContent = (group: CardsAndClassesGroup) =>
  group.cards.length > 0 || group.scheduledClasses.length > 0 || group.awaitingScheduling.length > 0;

const normalizeEmail = (email?: string | null) => email?.trim().toLocaleLowerCase() ?? "";

const fullName = (person: { firstName?: string | null; middleInitial?: string | null; lastName?: string | null }) =>
  [person.firstName, person.middleInitial, person.lastName].filter(Boolean).join(" ");

const isActiveCard = (expirationLocalDate: string) => {
  const expiration = parseISO(expirationLocalDate);
  const today = startOfDay(new Date());
  return isAfter(expiration, today) || isEqual(expiration, today);
};

const expiresSoon = (expirationLocalDate: string, expiresSoonLeadMs: number) => {
  const expiration = parseISO(expirationLocalDate);
  const today = startOfDay(new Date());
  const soonCutoff = addMilliseconds(today, expiresSoonLeadMs);
  return (
    (isAfter(expiration, today) || isEqual(expiration, today)) &&
    (isBefore(expiration, soonCutoff) || isEqual(expiration, soonCutoff))
  );
};

const toCardItem = (
  card: DustCard | DustMonitorCard,
  prefix: "DC" | "DM",
  isDustMonitorCard: boolean,
  expiresSoonLeadMs?: number,
): CardsAndClassesItem => {
  const soon = expiresSoonLeadMs != null && expiresSoon(card.expirationLocalDate, expiresSoonLeadMs);
  const veeStatus = isDustMonitorCard && (card as DustMonitorCard).hasPassedVee !== true ? " · VEE pending" : "";

  return {
    id: `${prefix}-${card.id}`,
    to: { name: prefix === "DC" ? routeNames.dustCard.list : routeNames.dustMonitorCard.list },
    primaryLabel: formatDustOrMonitorCardNumber(card.number),
    secondaryLabel: `${fullName(card)}${veeStatus}`,
    additionalInfo: soon ? "Expires soon" : "Expires",
    additionalInfoDetail: sysDate.shortDateString(card.expirationLocalDate) ?? card.expirationLocalDate,
    emphasizeAdditionalInfo: soon,
    sortValue: card.expirationLocalDate,
  };
};

const classDateTime = (classLocalDate: string, classLocalTime: string) =>
  [sysDate.shortDateString(classLocalDate), fromDateAndTimeToFormat(null, classLocalTime)].filter(Boolean).join(" ");

const classDateTimeDisplay = (item: DustClass | DustMonitorClass, label: "Dust Class" | "Dust Monitor Class") =>
  label === "Dust Monitor Class"
    ? getDustMonitorClassDisplayDate(item as DustMonitorClass)
    : classDateTime(item.classLocalDate, item.classLocalTime);

const classIdFromForm = (form: RegistrationForm, resourcePath: "dustClass" | "dustMonitorClass") => {
  if (form.parentEntityMurn) {
    const parts = getMurnParts(form.parentEntityMurn);
    if (parts.resourcePath === resourcePath && parts.id) return Number(parts.id);
  }

  if (resourcePath === "dustClass") {
    const primary = form.submissionData?.primaryForm as { dustClassId?: number } | undefined;
    return primary?.dustClassId;
  }

  return undefined;
};

const formCode = (form: RegistrationForm) => getMurnParts(form.formMurn).id;

const participantsFromForm = (form: RegistrationForm): RegistrationParticipant[] => {
  const primary = form.submissionData?.primaryForm;
  if (!primary) return [];

  if ("attendees" in primary && primary.attendees?.length) {
    return primary.attendees.map((attendee) => ({
      firstName: attendee.firstName,
      lastName: attendee.lastName,
      email: attendee.email,
      company: attendee.company,
      cardNumber: "dustMonitorCardNo" in attendee ? attendee.dustMonitorCardNo : undefined,
    }));
  }

  if ("firstName" in primary) {
    return [
      {
        firstName: primary.firstName,
        lastName: primary.lastName,
        email: primary.email,
        company: "companyName" in primary ? primary.companyName : undefined,
        cardNumber: "dustMonitorCardNo" in primary ? primary.dustMonitorCardNo : undefined,
      },
    ];
  }

  return [];
};

const ownerForParticipant = (participant: RegistrationParticipant, userEmail: string): OwnerGroup =>
  normalizeEmail(participant.email) === normalizeEmail(userEmail) ? "mine" : "others";

const buildClassDetails = (
  forms: RegistrationForm[],
  resourcePath: "dustClass" | "dustMonitorClass",
  userEmail: string,
) => {
  const details = new Map<number, ClassRegistrationDetails>();

  forms.forEach((form) => {
    const classId = classIdFromForm(form, resourcePath);
    if (!classId) return;

    const participants = participantsFromForm(form);
    const current = details.get(classId) ?? { owners: new Set<OwnerGroup>(), others: [] };
    participants.forEach((participant) => {
      const owner = ownerForParticipant(participant, userEmail);
      current.owners.add(owner);
      if (owner === "others") current.others.push(participant);
    });

    // 075b is explicitly a registration for others, including malformed legacy rows without attendees.
    if (formCode(form) === LE_CODES.ElectronicForm.ef075b) current.owners.add("others");
    details.set(classId, current);
  });

  return details;
};

const othersLabel = (participants: RegistrationParticipant[]) => {
  const names = participants.map(fullName).filter(Boolean);
  if (names.length === 1) return names[0];
  if (names.length > 1) return `${names[0]} + ${names.length - 1} more`;
  return "";
};

const registeredCount = (item: DustClass | DustMonitorClass, details?: ClassRegistrationDetails) => {
  const serverCount = (item as DustClass).registeredByCurrentUserCount;
  return serverCount != null ? serverCount : (details?.others.length ?? 0);
};

const toClassItem = (
  item: DustClass | DustMonitorClass,
  label: "Dust Class" | "Dust Monitor Class",
  owner: OwnerGroup,
  details?: ClassRegistrationDetails,
): CardsAndClassesItem => {
  const registered = registeredCount(item, details);

  return {
    id: `${label}-${owner}-${item.id}`,
    to: {
      name: label === "Dust Class" ? routeNames.dustClass.view : routeNames.dustMonitorClass.view,
      params: { id: item.id },
    },
    primaryLabel: `${label} · ${classDateTimeDisplay(item, label)}`,
    secondaryLabel: owner === "others" ? othersLabel(details?.others ?? []) || item.location : item.location,
    additionalInfo: owner === "others" && registered ? `${registered} registered` : undefined,
    sortValue: `${item.classLocalDate}T${item.classLocalTime}`,
  };
};

/**
 * Prefer the server's caller-relative ownership: a scoped class can be returned because the user
 * holds a seat, because they registered other people, or both. Neither the frozen submission JSON
 * nor `ParentEntityMurn` can answer this once staff reschedule a group's attendees into different
 * classes, which leaves the registrant owning several classes and attending none.
 *
 * Only when the server sent no ownership (staff reads, or a class with no matching attendee row)
 * do we fall back to inferring it from the registration form, then to "mine" so the class stays
 * visible rather than disappearing.
 */
const ownersForScopedClass = (item: DustClass | DustMonitorClass, details?: ClassRegistrationDetails) => {
  const dustClass = item as DustClass;
  const owners = new Set<OwnerGroup>();
  if (dustClass.isCurrentUserAttendee === true) owners.add("mine");
  if ((dustClass.registeredByCurrentUserCount ?? 0) > 0) owners.add("others");
  if (owners.size) return owners;

  return details?.owners.size ? details.owners : new Set<OwnerGroup>(["mine"]);
};

const isFutureClass = (item: DustClass | DustMonitorClass) => {
  if (!item.classLocalDate) return false;
  const classDate = parseISO(item.classLocalDate);
  const today = startOfDay(new Date());
  return isAfter(classDate, today) || isEqual(classDate, today);
};

const secondaryParticipantLabel = (participant: RegistrationParticipant) => {
  const details = [
    participant.company,
    participant.cardNumber ? formatDustOrMonitorCardNumber(participant.cardNumber) : undefined,
  ].filter(Boolean);
  return details.join(" · ");
};

const awaitingSchedulingItems = (
  forms: RegistrationForm[],
  owner: OwnerGroup,
  userEmail: string,
): CardsAndClassesItem[] =>
  forms
    .filter(
      (form) =>
        formCode(form) === LE_CODES.ElectronicForm.ef076 &&
        form.statusMurn === LE.ElectronicFormStatus.AwaitingScheduling,
    )
    .flatMap((form) => {
      const participants = participantsFromForm(form);
      // These forms were requested as the current user's own submissions, so a registration whose
      // participant cannot be read is still theirs and must not disappear from the pool.
      const rows = participants.length
        ? participants.filter((participant) => ownerForParticipant(participant, userEmail) === owner)
        : owner === "mine"
          ? [{} as RegistrationParticipant]
          : [];

      return rows.map((participant, index) => ({
        id: `awaiting-${form.id}-${index}`,
        to: { name: getElectronicFormRouteNameFromItem(form), params: { id: form.id } },
        primaryLabel: "Dust Monitor Class Registration",
        secondaryLabel: [
          fullName(participant) || `Electronic Form No. ${form.id}`,
          secondaryParticipantLabel(participant),
        ]
          .filter(Boolean)
          .join(" · "),
      }));
    });

export const buildCardsAndClassesGroups = ({
  dustCards,
  dustMonitorCards,
  dustClasses,
  dustMonitorClasses,
  registrationForms,
  userEmail,
  expiresSoonLeadMs,
}: {
  dustCards: DustCard[];
  dustMonitorCards: DustMonitorCard[];
  dustClasses: DustClass[];
  dustMonitorClasses: DustMonitorClass[];
  registrationForms: ElectronicForm[];
  userEmail: string;
  expiresSoonLeadMs?: number;
}): CardsAndClassesGroups => {
  const groups = emptyCardsAndClassesGroups();
  const forms = registrationForms as RegistrationForm[];
  const dustClassDetails = buildClassDetails(forms, "dustClass", userEmail);
  const paidDustMonitorForms = forms.filter(
    (form) => formCode(form) === LE_CODES.ElectronicForm.ef076 && form.statusMurn === LE.ElectronicFormStatus.Fulfilled,
  );
  const dustMonitorClassDetails = buildClassDetails(paidDustMonitorForms, "dustMonitorClass", userEmail);

  dustCards
    .filter((card) => isActiveCard(card.expirationLocalDate))
    .forEach((card) => {
      const owner = normalizeEmail(card.email) === normalizeEmail(userEmail) ? "mine" : "others";
      groups[owner].cards.push(toCardItem(card, "DC", false, expiresSoonLeadMs));
    });

  dustMonitorCards
    .filter((card) => isActiveCard(card.expirationLocalDate))
    .forEach((card) => {
      const owner = normalizeEmail(card.email) === normalizeEmail(userEmail) ? "mine" : "others";
      groups[owner].cards.push(toCardItem(card, "DM", true, expiresSoonLeadMs));
    });

  dustClasses.filter(isFutureClass).forEach((item) => {
    const details = dustClassDetails.get(item.id);
    ownersForScopedClass(item, details).forEach((owner) =>
      groups[owner].scheduledClasses.push(toClassItem(item, "Dust Class", owner, details)),
    );
  });

  dustMonitorClasses.filter(isFutureClass).forEach((item) => {
    const details = dustMonitorClassDetails.get(item.id);
    // The ownership-scoped API also returns PaymentPending reservations. Only a Fulfilled 076
    // belongs under Scheduled Classes; unpaid registrations remain in Electronic Forms.
    if (!details) return;
    ownersForScopedClass(item, details).forEach((owner) =>
      groups[owner].scheduledClasses.push(toClassItem(item, "Dust Monitor Class", owner, details)),
    );
  });

  groups.mine.awaitingScheduling = awaitingSchedulingItems(forms, "mine", userEmail);
  groups.others.awaitingScheduling = awaitingSchedulingItems(forms, "others", userEmail);

  ([groups.mine, groups.others] as CardsAndClassesGroup[]).forEach((group) => {
    group.cards.sort((a: CardsAndClassesItem, b: CardsAndClassesItem) =>
      (a.sortValue ?? "").localeCompare(b.sortValue ?? ""),
    );
    group.scheduledClasses.sort((a: CardsAndClassesItem, b: CardsAndClassesItem) =>
      (a.sortValue ?? "").localeCompare(b.sortValue ?? ""),
    );
    group.cards = group.cards.slice(0, HOME_SECTION_ITEM_LIMIT);
    group.scheduledClasses = group.scheduledClasses.slice(0, HOME_SECTION_ITEM_LIMIT);
    group.awaitingScheduling = group.awaitingScheduling.slice(0, HOME_SECTION_ITEM_LIMIT);
  });

  return groups;
};

export const classRegistrationFormMurns = [
  `murn:aqms2:lookup/ElectronicForm/${LE_CODES.ElectronicForm.ef075a}`,
  `murn:aqms2:lookup/ElectronicForm/${LE_CODES.ElectronicForm.ef075b}`,
  `murn:aqms2:lookup/ElectronicForm/${LE_CODES.ElectronicForm.ef076}`,
];
